import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Heart, MessageCircle, Send, User, Clock, CheckCircle,
  XCircle, HelpCircle, Shield
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow } from '@/lib/utils'
import ConfirmModal from '@/components/ConfirmModal'

interface SupportMatch {
  _id: string
  requesterName: string
  responderName?: string
  topic: string
  status: 'pending' | 'matched' | 'closed'
  messages: Array<{
    sender: string
    senderName: string
    content: string
    createdAt: string
  }>
  createdAt: string
  isAnonymous: boolean
}

export default function SupportMatchPage() {
  const { t } = useTranslation()
  const { isAuthenticated, user } = useAuthStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'request' | 'respond' | 'my'>('request')
  const [topic, setTopic] = useState('')
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [confirmModal, setConfirmModal] = useState<{open: boolean; title: string; description?: string; onConfirm: () => void}>({ open: false, title: '', onConfirm: () => {} })

  const { data: availableMatches } = useQuery({
    queryKey: ['support-matches-available'],
    queryFn: () => api.get('/support/matches/available').then(r => r.data),
    enabled: activeTab === 'respond' && isAuthenticated
  })

  const { data: myMatches } = useQuery({
    queryKey: ['support-matches-my'],
    queryFn: () => api.get('/support/matches/my').then(r => r.data),
    enabled: activeTab === 'my' && isAuthenticated
  })

  const selectedMatch = useMemo(() => myMatches?.find((m: SupportMatch) => m._id === selectedMatchId) || null, [myMatches, selectedMatchId])

  const requestMutation = useMutation({
    mutationFn: () => api.post('/support/match/request', { topic, isAnonymous: true }),
    onSuccess: () => {
      setTopic('')
      queryClient.invalidateQueries({ queryKey: ['support-matches-my'] })
      toast({ title: t('support.request.success') })
      setActiveTab('my')
    },
    onError: (err: any) => toast({
      title: err.response?.data?.error || t('support.error'),
      variant: 'destructive'
    })
  })

  const respondMutation = useMutation({
    mutationFn: (matchId: string) => api.post('/support/match/respond', { matchId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-matches-available'] })
      queryClient.invalidateQueries({ queryKey: ['support-matches-my'] })
      toast({ title: t('support.respond.success') })
      setActiveTab('my')
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const sendMessageMutation = useMutation({
    mutationFn: () => api.post(`/support/match/${selectedMatch?._id}/message`, { content: message }),
    onMutate: async () => {
      // Optimistically add the message to the selected match so it appears instantly
      const tempId = `temp-${Date.now()}`
      const optimisticMessage = {
        sender: user?.id || '',
        senderName: user?.username || '',
        content: message.trim(),
        createdAt: new Date().toISOString(),
      }
      await queryClient.cancelQueries({ queryKey: ['support-matches-my'] })
      const previousMatches = queryClient.getQueryData(['support-matches-my'])
      queryClient.setQueryData(['support-matches-my'], (old: any) => {
        if (!old || !Array.isArray(old)) return old
        return old.map((m: SupportMatch) =>
          m._id === selectedMatchId
            ? { ...m, messages: [...(m.messages || []), optimisticMessage] }
            : m
        )
      })
      return { previousMatches, tempId }
    },
    onSuccess: (_data, _variables) => {
      setMessage('')
      // Replace optimistic temp with server-confirmed data and refresh list
      queryClient.invalidateQueries({ queryKey: ['support-matches-my'] })
    },
    onError: (err: any, _variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousMatches) {
        queryClient.setQueryData(['support-matches-my'], context.previousMatches)
      }
      toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
    },
  })

  const closeMutation = useMutation({
    mutationFn: (matchId: string) => api.put(`/support/match/${matchId}/close`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-matches-my'] })
      toast({ title: t('support.chat.closeSuccess') })
      setSelectedMatchId(null)
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('support.error'), variant: 'destructive' })
  })

  const askConfirm = (title: string, description: string, onConfirm: () => void) => {
    setConfirmModal({ open: true, title, description, onConfirm })
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <Shield className="w-16 h-16 text-primary mx-auto mb-6" />
        <h1 className="font-display text-2xl font-bold mb-4">{t('support.title')}</h1>
        <p className="text-muted-foreground mb-8">
          {t('support.loginRequired')}
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 relative">
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        description={confirmModal.description}
        variant="warning"
        onConfirm={() => { confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, open: false }) }}
        onCancel={() => setConfirmModal({ ...confirmModal, open: false })}
      />

      {/* Safe Exit */}
      <button
        onClick={() => window.location.href = 'https://tr.weather.com'}
        className="fixed top-20 right-4 z-40 px-3 py-1.5 rounded-full bg-muted text-xs font-medium border border-border hover:bg-secondary transition-colors shadow-sm"
      >
        {t('common.safeExit')}
      </button>

      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-7 h-7 text-primary" />
        </div>
        <h1 className="font-display text-2xl font-bold">{t('support.title')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('support.subtitle')}
        </p>
      </div>

      <div className="flex gap-2 mb-6 p-1 rounded-xl bg-secondary/50">
        {[
          { key: 'request', label: t('support.tab.request'), icon: HelpCircle },
          { key: 'respond', label: t('support.tab.respond'), icon: Heart },
          { key: 'my', label: t('support.tab.my'), icon: MessageCircle },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key as any); setSelectedMatchId(null) }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-card shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'request' && (
          <motion.div
            key="request"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-6 rounded-2xl bg-card border border-border"
          >
            <h2 className="font-semibold mb-4">{t('support.request.title')}</h2>
            <p className="text-sm text-muted-foreground mb-4">
              {t('support.request.description')}
            </p>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t('support.request.placeholder')}
              className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[100px] mb-4"
            />
            <Button
              onClick={() => requestMutation.mutate()}
              disabled={!topic.trim() || requestMutation.isPending}
              className="w-full rounded-lg"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              {requestMutation.isPending ? t('support.request.submitting') : t('support.request.submit')}
            </Button>
          </motion.div>
        )}

        {activeTab === 'respond' && (
          <motion.div
            key="respond"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            {availableMatches?.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {t('support.respond.empty')}
              </div>
            )}
            {availableMatches?.map((match: SupportMatch) => (
              <div key={match._id} className="p-4 rounded-xl bg-card border border-border flex items-center justify-between">
                <div>
                  <span className="text-xs text-primary font-medium">{t('support.respond.topicLabel')}</span>
                  <p className="font-medium">{match.topic}</p>
                  <span className="text-xs text-muted-foreground">{formatDistanceToNow(match.createdAt)}</span>
                </div>
                <Button
                  size="sm"
                  onClick={() => respondMutation.mutate(match._id)}
                  disabled={respondMutation.isPending}
                >
                  <Heart className="w-4 h-4 mr-1" /> {t('support.respond.button')}
                </Button>
              </div>
            ))}
          </motion.div>
        )}

        {activeTab === 'my' && (
          <motion.div
            key="my"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {selectedMatch ? (
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{selectedMatch.topic}</h3>
                    <span className={`text-xs ${
                      selectedMatch.status === 'matched' ? 'text-green-500' : 'text-muted-foreground'
                    }`}>
                      {selectedMatch.status === 'matched' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                      {selectedMatch.status === 'closed' && <XCircle className="w-3 h-3 inline mr-1" />}
                      {selectedMatch.status === 'pending' && <Clock className="w-3 h-3 inline mr-1" />}
                      {selectedMatch.status === 'matched' ? t('support.status.active') : selectedMatch.status === 'closed' ? t('support.status.closed') : t('support.status.pending')}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedMatchId(null)}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
                <div className="h-80 overflow-y-auto p-4 space-y-3">
                  {selectedMatch.messages?.map((msg: SupportMatch['messages'][number], i: number) => (
                    <div
                      key={i}
                      className={`flex ${msg.sender === user?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                        msg.sender === user?.id
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : 'bg-secondary rounded-bl-md'
                      }`}>
                        <p className="text-xs opacity-70 mb-1">{msg.senderName}</p>
                        <p>{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {(!selectedMatch.messages || selectedMatch.messages.length === 0) && (
                    <div className="text-center text-muted-foreground text-sm py-8">
                      {t('support.chat.noMessages')}
                    </div>
                  )}
                </div>
                {selectedMatch.status === 'matched' && (
                  <div className="p-4 border-t border-border space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && sendMessageMutation.mutate()}
                        placeholder={t('support.chat.placeholder')}
                        className="flex-1 px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <Button
                        size="icon"
                        onClick={() => sendMessageMutation.mutate()}
                        disabled={!message.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                    <button
                      onClick={() => askConfirm(t('support.chat.closeButton'), t('support.chat.closeConfirm'), () => closeMutation.mutate(selectedMatch._id))}
                      className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                    >
                      {t('support.chat.closeButton')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {myMatches?.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    {t('support.my.empty')}
                  </div>
                )}
                {myMatches?.map((match: SupportMatch) => (
                  <button
                    key={match._id}
                    onClick={() => setSelectedMatchId(match._id)}
                    className="w-full p-4 rounded-xl bg-card border border-border text-left hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{match.topic}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <User className="w-3 h-3" />
                          {match.responderName ? `${match.requesterName} ↔ ${match.responderName}` : match.requesterName}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        match.status === 'matched'
                          ? 'bg-green-500/10 text-green-500'
                          : match.status === 'pending'
                          ? 'bg-yellow-500/10 text-yellow-500'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {match.status === 'matched' ? t('support.status.active') : match.status === 'pending' ? t('support.status.pending') : t('support.status.closed')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
